import type { ReactNode } from 'react'

import {
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowLeftRegular } from '@enechain/ecloud-icons'

import { CsvUploadPanel } from '~/components/common/CsvUpload/CsvUploadPanel'
import { iconSize } from '~/constants/iconSize'

type Props = {
  /** 「ファイル選択に戻る」(AC-DUC-034) の文言と操作。 */
  back: { label: string; onClick: () => void; disabled?: boolean }
  /** 確定モーダルを開くボタンの文言と操作。 押せない理由はパネル内のバナーが示す。 */
  confirm: { label: string; onClick: () => void; disabled: boolean }
  /** パネル見出し (design `.up-panel-title`「差分プレビュー」)。 */
  title: string
  /** 見出し直下の説明 (design `.up-panel-hint`)。 */
  hint: ReactNode
  /** 説明より下の中身 (読み込み中表示・バナー・件数サマリ・差分表)。 */
  children: ReactNode
}

/**
 * CSV アップロード Step2 (差分プレビュー) の外枠
 * (design `admin/uploads/*.html` の `#panel-step2`)。
 *
 * 白地の `.up-panel` (`CsvUploadPanel`) の中に、 上端の操作バー (`.up-actions-top`: 左に戻る・右に確定) →
 * 見出し → 説明 → 中身 の順で並べる。 操作バーはパネルの padding を打ち消して
 * 枠いっぱいに敷き、 縦に長い差分表をスクロールしても確定ボタンに手が届くよう
 * スクロール領域の上端に貼り付ける (design の `position: sticky; top: 0`)。
 * ボタンは design どおり M サイズ (40px) で左右をそろえる。
 */
export const CsvDiffPanel = ({
  back,
  confirm,
  title,
  hint,
  children,
}: Props): React.ReactElement => (
  <CsvUploadPanel>
    <HStack
      position="sticky"
      zIndex="docked"
      top={0}
      justifyContent="space-between"
      marginTop={-4}
      marginBottom={3}
      padding={3}
      background="Surface.primary"
      borderBottomWidth="1px"
      borderBottomColor="Border.lowEmphasis"
      borderTopRadius="lg"
      marginX={-4}
    >
      <TertiaryButton disabled={back.disabled} onClick={back.onClick} size="md">
        <FaArrowLeftRegular boxSize={iconSize.labeledButton} />
        {back.label}
      </TertiaryButton>
      <PrimaryButton
        disabled={confirm.disabled}
        onClick={confirm.onClick}
        size="md"
      >
        {confirm.label}
      </PrimaryButton>
    </HStack>
    <Text as="h2" textStyle="uploadPanelTitle" marginBottom={3}>
      {title}
    </Text>
    <Text marginBottom={3} color="Text.midEmphasis" fontSize="md">
      {hint}
    </Text>
    {children}
  </CsvUploadPanel>
)
